import { Dialog, Typography, Box, Paper, Grid } from "@material-ui/core";
import { ReactElement, useContext, useEffect } from "react";
import { useRouter } from "next/router";
import styled from "@emotion/styled";

import { ButtonLink } from "@/components/Buttons/";
import { useIsMedium } from "@/styles/breakpoints";
import { IsBusinessContext } from "../contexts";

const PageVersionButton = styled(ButtonLink)`
  display: block;
  font-size: 24px;
  margin: 32px 4px;
  width: 200px;

  @media (min-width: 540px) {
    width: 371px;
  }
`;

const WelcomeDialogPaper = styled(Paper)`
  height: 773px;
  padding: 1em;
  background: linear-gradient(95.96deg, #e5e7ff 7.11%, #fff8da 96.38%);
  border-radius: 6px;
`;

const WelcomeDialogContainer = styled(Grid)`
  height: 100%;
  align-items: center;
`;

const PresentationalImage = styled.img`
  width: 100%;
  mix-blend-mode: multiply;
`;

const BackgroundImageContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
`;

const BackgroundImage = styled.img`
  float: right;
  width: 400px;
  display: none;

  @media (min-width: 768px) {
    display: block;
  }
`;

type IsBusinessModalProps = {
  businessRoute: string;
  businessComponent: ReactElement;
  brokerRoute: string;
  brokerComponent: ReactElement;
  forceShow?: boolean;
};

export default function IsBusinessModal({
  businessRoute,
  businessComponent,
  brokerRoute,
  brokerComponent,
  forceShow,
}: IsBusinessModalProps) {
  const isMedium = useIsMedium();
  const router = useRouter();
  const { isBusiness, setIsBusiness } = useContext(IsBusinessContext);

  // Redirect already-logged-in users to the appropriate business/broker side
  // of the site.
  // Queue a redirect, but render the page immediately to avoid the modal
  // appearing for a second.
  useEffect(() => {
    const newPath = isBusiness ? businessRoute : brokerRoute;
    if (!forceShow && isBusiness !== null && router.pathname !== newPath) {
      router.push(newPath);
    }
  }, [isBusiness]);
  if (!forceShow && isBusiness !== null) {
    return isBusiness ? businessComponent : brokerComponent;
  }

  return (
    <Dialog open maxWidth="lg" PaperComponent={WelcomeDialogPaper}>
      <BackgroundImageContainer>
        <BackgroundImage
          src="/images/welcome_modal_background_waves.png"
          alt="Welcome modal background waves"
        />
      </BackgroundImageContainer>
      <WelcomeDialogContainer container>
        {isMedium && (
          <Grid item md={6}>
            <PresentationalImage
              src="/images/woman_at_counter.png"
              alt="Woman at counter"
            />
          </Grid>
        )}
        <Grid item md={6}>
          <Box margin={4}>
            <Typography variant="h2" color="primary" style={{ fontSize: 48 }}>
              Welcome!
            </Typography>
            <Typography variant="subtitle2">
              Please select your client portal:
            </Typography>
          </Box>
          <Box margin={4} mt={8}>
            <PageVersionButton
              route={businessRoute}
              onClick={() => setIsBusiness(true)}
              color="primary"
            >
              Businesses
            </PageVersionButton>
            <PageVersionButton
              route={brokerRoute}
              onClick={() => setIsBusiness(false)}
              color="primary"
            >
              Insurance Brokers
            </PageVersionButton>
          </Box>
        </Grid>
      </WelcomeDialogContainer>
    </Dialog>
  );
}
